import { useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion/Reveal";
import EarlyAccessButton from "@/components/EarlyAccessButton";
import EarlyAccessForm from "@/components/EarlyAccessForm";
import Marquee from "@/components/Marquee";

const GALLERY = [
  { src: "https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/f96pa1gb_Model2.png", cap: "On Skin" },
  { src: "https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/5qqris7f_Model1.png", cap: "The Finish" },
  { src: "https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/ic9dn3oy_Underbloom%20Package%20Blur.png", cap: "The Compact" },
  { src: "https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/8e0gnz3e_Underbloom%20Swatch.png", cap: "The Puff" },
  { src: "https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/qrbdb3su_Underbloom_Ingredients_Wide.png", cap: "The Ingredients" },
];

export default function Underbloom() {
  const [active, setActive] = useState(0);
  return (
    <div data-testid="underbloom-page">
      {/* Product hero */}
      <section data-testid="product-hero" className="bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24 grid md:grid-cols-12 gap-8 md:gap-14">
          {/* Gallery */}
          <div className="md:col-span-7">
            <div className="flex gap-3 md:gap-4">
              {/* Thumbnail rail — vertical, left of main image */}
              <div className="flex flex-col gap-3 md:gap-4 w-16 md:w-24 shrink-0">
                {GALLERY.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`product-thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`overflow-hidden border ${active === i ? "border-ink" : "border-sage"} transition-colors`}
                    aria-label={g.cap}
                  >
                    <img src={g.src} alt={g.cap} className="w-full aspect-square object-cover" loading="lazy" />
                  </button>
                ))}
              </div>

              {/* Main display */}
              <div className="relative bg-snow border border-sage aspect-[4/5] overflow-hidden flex-1">
                <img
                  src={GALLERY[active].src}
                  alt={GALLERY[active].cap}
                  className="w-full h-full object-cover"
                  data-testid="product-gallery-main"
                />
                <div className="absolute bottom-4 left-4 font-mono-stamp text-pencil bg-ivory/40 backdrop-blur-[2px] px-2 py-1">
                  Preliminary design · Subject to change
                </div>
              </div>
            </div>
          </div>

          {/* Buy panel */}
          <div className="md:col-span-5 md:pt-4">
            <Reveal>
              <p className="font-mono-stamp text-brass">Finishing Powder · Talc-Free</p>
              <p className="font-display text-2xl mt-6" style={{ fontWeight: 400 }}>Underbloom</p>
              <p className="text-pencil text-sm mt-1">
                Scented in{" "}
                <Link to="/fragrance" data-testid="product-fragrance-link" className="link-underline text-ink">
                  Whisper
                </Link>
              </p>
              <h1 className="font-display text-6xl md:text-7xl leading-[1.12] mt-6" style={{ fontWeight: 300 }}>
                Finish, <em className="italic">refined.</em>
              </h1>
              <p className="font-display italic text-2xl text-pencil mt-4">The finish that disappears.</p>
              <p className="mt-8 text-[15px] leading-relaxed">
                A talc-free, refillable finishing powder for warm skin and the places it folds. Invisible, silky,
                quietly scented. Our first product, in development now.
              </p>

              <div className="mt-10 border-t border-b border-ink py-6 space-y-4">
                <div className="flex justify-between items-center">
                  <p className="font-mono-stamp text-pencil">Availability</p>
                  <p className="font-mono-stamp text-ink">Arriving 2027</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="font-mono-stamp text-pencil">Format</p>
                  <p className="font-mono-stamp text-ink">Refillable Compact + Refills</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="font-mono-stamp text-pencil">Price</p>
                  <p className="font-mono-stamp text-ink">Set at Launch</p>
                </div>
                <p className="text-pencil text-xs pt-2">
                  Early-access pricing for the founding list — set at launch.
                </p>
              </div>

              <div className="mt-8">
                <EarlyAccessButton label="Request early access" data-testid="product-buy-cta" />
                <p className="text-pencil text-xs mt-3">
                  No noise. Occasional letters from the house.
                </p>
              </div>

              <div className="mt-10 flex flex-wrap gap-6 pt-6 border-t border-sage">
                {["Talc-free", "Refillable", "Small batch"].map((t) => (
                  <div key={t} className="flex items-center gap-3">
                    <span className="w-6 h-px bg-brass" />
                    <p className="font-mono-stamp text-ink">{t}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* What it does */}
      <section data-testid="product-what" className="bg-cream">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Reveal>
              <p className="font-mono-stamp text-pencil">Chapter I</p>
              <p className="eyebrow eyebrow-brass mt-3">What It Does</p>
            </Reveal>
          </div>
          <div className="md:col-span-9 space-y-14">
            {[
              { n: "01", h: "Invisible, dry-touch finish on every skin tone.", b: "Blurs to an invisible, silky finish with no white cast, designed to disappear on every skin tone." },
              { n: "02", h: "Softens friction where skin meets skin.", b: "For the under-bust, bra-line, inner thigh, and lower back." },
              { n: "03", h: "Absorbs moisture so skin stays silky, not chalky.", b: "Skin feels dry and smooth to the touch, never powdery." },
              { n: "04", h: "A breathe of Whisper, felt more than smelled.", b: "A near-skin scent that sits under your own fragrance, never competes." },
              { n: "05", h: "Refillable by design.", b: "A sealed pod drops into the compact. Keep the case, replace the powder." },
            ].map((m, i) => (
              <Reveal key={m.n} delay={i * 0.08}>
                <div className="grid md:grid-cols-12 gap-6 border-t border-ink/15 pt-8">
                  <p className="md:col-span-2 font-mono-stamp text-brass">{m.n}</p>
                  <div className="md:col-span-10">
                    <p className="font-display text-3xl md:text-5xl leading-[1.18]" style={{ fontWeight: 300 }}>
                      {m.h}
                    </p>
                    <p className="mt-4 text-pencil text-[15px] max-w-2xl leading-relaxed">{m.b}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial phrase */}
      <section data-testid="product-marquee" className="border-y border-sage bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20 flex justify-center items-center">
          <p className="font-display text-ink text-center" style={{ fontSize: "clamp(38px, 6vw, 96px)", lineHeight: 1, fontWeight: 300 }}>
            Composed, <em className="italic">all day</em>
          </p>
        </div>
      </section>

      {/* How to use */}
      <section data-testid="product-use" className="bg-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-mono-stamp text-pencil">Chapter II</p>
            <p className="eyebrow eyebrow-brass mt-3">How to Use</p>
            <h3 className="font-display text-5xl md:text-6xl mt-8 leading-[1.12]" style={{ fontWeight: 300 }}>
              Press <em className="italic">lightly.</em>
            </h3>
          </div>
          <div className="md:col-span-7 md:pl-10">
            <p className="text-[16px] leading-[1.8] max-w-xl">
              Press lightly where skin turns warm or damp — the folds, the collar, the day's pressure points.
              A little sets; the rest disappears. Reach for it without thinking.
            </p>
          </div>
        </div>
      </section>

      {/* Formula */}
      {/* Chapter III — The Formula — hidden for now
      <section data-testid="product-formula" className="bg-ink text-ivory">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-mono-stamp text-brass">Chapter III</p>
            <p className="eyebrow mt-3" style={{ color: "#AFA268" }}>The Formula</p>
            <h3 className="font-display text-5xl md:text-6xl mt-8 leading-[1.12]" style={{ fontWeight: 300 }}>
              Kept low, <em className="italic">kept dry.</em>
            </h3>
          </div>
          <div className="md:col-span-7 md:pl-10 space-y-6">
            <p className="text-[15px] leading-relaxed text-ivory/85 max-w-xl">
              Talc-free and quietly scented in Whisper, kept low and dry so it flatters skin rather than sitting on it.
              Developed slowly; nothing ships until it earns its place. Full ingredient list published at launch.
            </p>
            <div className="border border-ivory/15 p-6 md:p-8">
              <p className="font-mono-stamp text-ivory/60 mb-4">Preliminary INCI · Full list at launch</p>
              <p className="font-mono-stamp text-ivory/85 leading-relaxed">
                MICA · TAPIOCA STARCH · CAPRYLIC/CAPRIC TRIGLYCERIDE · SILICA · ORRIS ROOT EXTRACT ·
                AMBRETTE SEED OIL · TOCOPHEROL · [PARFUM — WHISPER]
              </p>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Scented in Whisper — Chapter III */}
      <section data-testid="product-whisper" className="bg-bone">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <img
              src="https://customer-assets.emergentagent.com/job_underbloom-launch/artifacts/sigl3njh_Ingredients.png"
              alt="Whisper — ambrette, orris"
              className="w-full h-[500px] object-cover"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-6 md:pl-10">
            <p className="font-mono-stamp text-pencil">Chapter III</p>
            <p className="eyebrow eyebrow-brass mt-3">Scented in Whisper</p>
            <h3 className="font-display text-5xl md:text-6xl mt-6 leading-[1.12]" style={{ fontWeight: 300 }}>
              A note that <em className="italic">flatters.</em>
            </h3>
            <p className="mt-8 text-[15px] leading-relaxed max-w-lg">
              Underbloom carries Whisper — ambrette and warm skin, orris and clean white musk, vanilla kept low and dry.
              Expensive skin, never dessert.
            </p>
            <div className="mt-8">
              <Link to="/fragrance" data-testid="product-read-fragrance" className="btn-ghost">
                Read the fragrance →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Join section removed */}
    </div>
  );
}
